import { useCallback, useEffect, useState } from "react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { Card } from "../../components/ui/card.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion.js";
import { Input } from "../../components/ui/input.js";
import { SelectField } from "../../components/ui/select-field.js";
import { Badge } from "../../components/ui/badge.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { FeatureCard, type AlertRule, type Metric } from "./FeatureCard.js";

/**
 * The order the features read in: shortfalls, long days, then the two events.
 *
 * There is no master switch and no shared mailbox: each card's own Enabled tick is
 * the only thing that turns a feature on, and each card chooses its own recipients.
 */
const CARD_ORDER: Metric[] = [
  "INSUFFICIENT_HOURS", "OVERTIME_APPROVAL", "WEEKEND_WORK", "HOLIDAY_WORK", "LEAVE_WORK", "WEEKLY_HOURS", "SHIFT_CHANGE",
];

// ─── Developer test harness ──────────────────────────────────────────────────

interface TestResult { type: string; label: string; status: string; detail: string }

const STATUS_TONE: Record<string, "success" | "warning" | "error" | "outline"> = {
  sent: "success",
  suppressed: "warning",
  "missing-template": "error",
  error: "error",
};

function TestEmailHarness() {
  const { show } = useToast();
  const [to, setTo] = useState("");
  const [type, setType] = useState("");
  const [templates, setTemplates] = useState<Array<{ type: string; label: string }>>([]);
  const [report, setReport] = useState<{ emailConfigured: boolean; results: TestResult[] } | null>(null);

  useEffect(() => {
    api.get<Array<{ type: string; label: string }>>("/admin/attendance-alert-templates")
      .then((r) => setTemplates(r.data))
      .catch((e) => logError("TestEmailHarness.templates", e));
  }, []);

  async function send() {
    if (!to.trim()) { show("Enter an address to send to", "error"); throw new Error("validation"); }
    try {
      const { data } = await api.post<{ emailConfigured: boolean; results: TestResult[] }>(
        "/admin/attendance-alert-test-email",
        type ? { to: to.trim(), type } : { to: to.trim() },
      );
      setReport(data);
    } catch (e) {
      logError("TestEmailHarness.send", e);
      show(errMsg(e) ?? "Could not send the test", "error");
      throw e;
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[12px] text-surface-500">
        Sends a sample of each attendance email, with placeholder data, to one address.
        Nothing is written and no real recipient is contacted.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">Send to</span>
          <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@company.com" className="w-72" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">Template</span>
          <SelectField value={type} onChange={(e) => setType(e.target.value)} size="sm" className="w-64">
            <option value="">All attendance emails</option>
            {templates.map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
          </SelectField>
        </label>
        <AsyncButton size="sm" variant="info" onClick={() => send()}>Send test</AsyncButton>
      </div>

      {report && (
        <div className="flex flex-col gap-2">
          {!report.emailConfigured && (
            // Without this the harness would look like it worked: the server's send
            // is a silent no-op when SES_FROM_EMAIL is unset.
            <div className="rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-[12px] text-[var(--color-warning-fg)]">
              <strong>Email delivery is switched off on this server</strong> (SES_FROM_EMAIL is unset).
              Templates below were rendered and checked, but nothing was actually sent.
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template</TableHead>
                <TableHead className="w-32">Result</TableHead>
                <TableHead>Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.results.map((r) => (
                <TableRow key={r.type}>
                  <TableCell className="text-[13px]">{r.label}</TableCell>
                  <TableCell><Badge tone={STATUS_TONE[r.status] ?? "outline"}>{r.status}</Badge></TableCell>
                  <TableCell className="text-[12px] text-surface-600">{r.detail}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ─── Panel ───────────────────────────────────────────────────────────────────

export default function AttendanceAlertsPanel() {
  const { show } = useToast();
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<AlertRule[]>("/admin/attendance-alert-rules");
      setRules(data);
    } catch (e) {
      logError("AttendanceAlertsPanel.load", e);
      show("Could not load attendance alert settings", "error");
    } finally { setLoading(false); }
  }, [show]);

  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return <Card className="p-5 text-[13px] text-surface-500">Loading attendance alerts…</Card>;
  }

  const byMetric = new Map(rules.map((r) => [r.metric, r]));
  const ordered = CARD_ORDER.map((m) => byMetric.get(m)).filter((r): r is AlertRule => !!r);
  const missing = CARD_ORDER.filter((m) => !byMetric.has(m));

  return (
    <div className="flex flex-col gap-5">
      {ordered.map((r) => <FeatureCard key={r.id} rule={r} onSaved={() => void load()} />)}

      {missing.length > 0 && (
        // Every feature should have a row from the migration; if one is absent its
        // card cannot render, so say so rather than silently showing fewer features.
        <Card className="p-5 text-[13px] text-[var(--color-warning-fg)]">
          Missing configuration for: {missing.join(", ")}. Re-run the attendance alerts migration.
        </Card>
      )}

      {/* Collapsed by default — a diagnostic tool, not part of the daily job. */}
      <Card>
        <Accordion type="single" collapsible>
          <AccordionItem value="dev" className="border-b-0">
            <AccordionTrigger className="px-5">
              <span className="flex items-center gap-2">
                <span className="text-[15px] font-bold">Developer tools</span>
                <Badge tone="outline">testing only</Badge>
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <TestEmailHarness />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>
    </div>
  );
}
